import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import { DeleteMultipleWorkOrdersModal } from "@/shared/work-orders-modals/components/modal-delete-multiple-work-orders";
import { DeleteWorkOrderModal } from "@/shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import { OpenWorkOrderModal } from "@/shared/work-orders-modals/components/modal-open-work-order";
import {
	type ResourceType,
	TableWorkOrdesResoures,
} from "@/shared/work-orders-modals/components/work-order-resources/table-work-ordes-resoures";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import {
	AssetTreePicker,
	dxtToLocalServerTime,
	ErrorModal,
	useUserContext,
} from "@dexteel/mesf-core";
import { Button, Paper, Snackbar, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { Alert } from "@mui/lab";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	ICellRendererParams,
	IContextMenuParams,
	ITooltipParams,
	MenuItemDef,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import { getCraftLetter } from "@/utils/craftLetter";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../components/loaders/LazyLoading/LazyLoading";
import { AssetNameColumnColumn } from "./components/assetName-column";
import { CloseGroupWorkOrdersDialog } from "./components/close-group-wo-dialog/close-group-wo-dialog";
import { WorkOrdersCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { WorkOrdersResourcesColumn } from "./components/resources-buttons/resources-buttons.columns";
import { useWorkOrdersContext } from "./context/workOrdersContext";
import { Filters } from "./filters";
import { useRefreshWorkOrders, useWorkOrders } from "./hooks/useWorkOrders";
import { useWorkOrdersFilters } from "./hooks/useWorkOrdersFilters";
import type { WorkOrder } from "./models/WorkOrder";
import { FilterPaper } from "@/components/page/FilterPaper";

export default function WorkOrders() {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: {
			assetCodes,
			listWorkOrderSelected,
			showDialogToCloseGroup,
			openWorkOrderResources,
		},
		actions: {
			setListWorkOrderSelected,
			setShowDialogToCloseGroup,
			setOpenWorkOrderResources,
		},
	} = useWorkOrdersContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState<string>("");

	const filters = useWorkOrdersFilters();
	const AssetId = filters.assetId;
	const {
		rows: rowData,
		isLoading,
		error: workOrdersError,
		noResults,
	} = useWorkOrders(filters);
	const refreshWorkOrders = useRefreshWorkOrders();
	const [noResultsDismissed, setNoResultsDismissed] = useState(false);
	const [dismissedListError, setDismissedListError] = useState<Error | null>(
		null,
	);

	const [selectedRow, setSelectedRow] = useState(INITIAL_VALUES_MODAL);
	const [selectedRows, setSelectedRows] = useState<WorkOrder[]>([]);

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [selectedResource, setSelectedResource] = useState<ResourceType | null>(
		null,
	);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const gridRef = useRef<AgGridReact>(null);

	const [openWorkOrderEditModal, setOpenWorkOrderEditModal] =
		useState<boolean>(false);
	const [openCloseWorkOrderModal, setOpenCloseWorkOrderModal] =
		useState<boolean>(false);
	const [openWorkOrderModal, setOpenWorkOrderModal] = useState<boolean>(false);
	const [openDeleteWorkOrderModal, setOpenDeleteWorkOrderModal] =
		useState<boolean>(false);
	const [
		openDeleteMultipleWorkOrdersModal,
		setOpenDeleteMultipleWorkOrdersModal,
	] = useState<boolean>(false);

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setNoResultsDismissed(true);
	};

	const onMenuOptionSelected = (
		option:
			| "EDIT"
			| "EDIT-COMMENTS"
			| "CLOSE-ORDER"
			| "OPEN-ORDER"
			| "DELETE-ORDER"
			| "DELETE-MULTIPLE-ORDER",
		row: any,
	) => {
		if (option !== "DELETE-MULTIPLE-ORDER") {
			setSelectedRow(row);
		} else if (option === "DELETE-MULTIPLE-ORDER") {
			setSelectedRows(
				(row as RowNode<WorkOrder>[])
					.map((node) => node.data)
					.filter((data): data is WorkOrder => data !== undefined),
			);
		}

		switch (option) {
			case "EDIT":
			case "EDIT-COMMENTS":
				setOpenWorkOrderEditModal(true);
				break;
			case "CLOSE-ORDER":
				setOpenCloseWorkOrderModal(true);
				break;
			case "OPEN-ORDER":
				setOpenWorkOrderModal(true);
				break;
			case "DELETE-ORDER":
				setOpenDeleteWorkOrderModal(true);
				break;
			case "DELETE-MULTIPLE-ORDER":
				setOpenDeleteMultipleWorkOrdersModal(true);
				break;
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>
							{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}
				>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}
					>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderExpiredCell = (props: ICellRendererParams) => {
		if (!props.data?.IsExpired) return null;
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: "#800020",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>E</div>
				</div>
			</div>
		);
	};

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const listOfWorkOrders = event.api.getSelectedNodes();
		setListWorkOrderSelected(listOfWorkOrders);
	}, []);

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const openResourceModal = useCallback(
		(resource: ResourceType, rowData: any) => {
			setSelectedRow(rowData);
			setSelectedResource(resource);
			setOpenWorkOrderResources(true);
		},
		[],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const status = params.node?.data.WorkOrderStatusCode;
			const typeCode = params.node?.data.TypeCode;
			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit",
					action: () => {
						onMenuOptionSelected("EDIT-COMMENTS", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
			];

			if (status === "O") {
				result.push({
					name: "Close Work Order",
					action: () => {
						if (listWorkOrderSelected?.length > 1) {
							setShowDialogToCloseGroup(true);
						} else onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}

			if (status === "C") {
				result.push({
					name: "Open Work Order",
					action: () => {
						onMenuOptionSelected("OPEN-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-plus-square"></i>',
				});
			}

			if (typeCode === "PM" && params.node?.data.TemplateId) {
				result.push({
					name: "Go To Template",
					action: () => {
						const templateId = params.node?.data.TemplateId;
						const assetId = params.node?.data.AssetId;
						navigate(`/templates/${assetId}?templateId=${templateId}`);
					},
					icon: '<i class="fas fa-file-alt"></i>',
				});
			}

			result.push(
				"separator",
				{
					name: "Work Order Documents",
					action: () => openResourceModal("DRAWINGS", params.node?.data),
					icon: '<i class="far fa-file-image"></i>',
				},
				{
					name: "Work Order Parts",
					action: () => openResourceModal("PARTS", params.node?.data),
					icon: '<i class="fas fa-tools"></i>',
				},
				{
					name: "Work Order Attachments",
					action: () => openResourceModal("ATTACHMENTS", params.node?.data),
					icon: '<i class="fas fa-paperclip"></i>',
				},
				{
					name: "Work Order LOTO",
					action: () => openResourceModal("LOTO", params.node?.data),
					icon: '<i class="fas fa-tag"></i>',
				},
				"separator",
			);

			if (params.api.getSelectedNodes().length > 1) {
				result.push({
					name: "Delete Work Orders",
					action: () => {
						onMenuOptionSelected(
							"DELETE-MULTIPLE-ORDER",
							params.api.getSelectedNodes() as RowNode<WorkOrder>[],
						);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			} else {
				result.push({
					name: "Delete Work Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return params.node?.data.WorkOrderId ? result : [];
		},
		[listWorkOrderSelected, openResourceModal, navigate],
	);

	const onDobleClicked = () => {
		const selectedRow1 = gridRef.current!.api.getSelectedNodes();
		onMenuOptionSelected("EDIT", selectedRow1[0].data);
	};

	const changeAsset = (value: number | null) => {
		navigate(
			{ pathname: `/work-orders/${value}`, search: searchParams.toString() },
			{ replace: true },
		);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			refreshWorkOrders();
		}
	};

	useEffect(() => {
		setColumnDefs([
			{
				colId: "checkbox",
				flex: 1,
				maxWidth: 33,
				filter: false,
				checkboxSelection: true,
				headerCheckboxSelection: true,
			},
			{
				field: "WorkOrderName",
				headerName: "Name",
				minWidth: 100,
				flex: 1,
				headerClass: "ag-center-header",
			},
			{
				field: "Asset",
				headerName: "Asset",
				cellRenderer: AssetNameColumnColumn,
				minWidth: 150,
				flex: 2,
				valueGetter: (params) => params.data.FullPath,
			},
			{ field: "Title", headerName: "Title", minWidth: 170, flex: 3 },
			{
				field: "OpenTimestamp",
				headerName: "Open",
				minWidth: 110,
				maxWidth: 110,

				flex: 1,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
			},
			{
				field: "CloseTimestamp",
				headerName: "Closed",
				minWidth: 110,
				maxWidth: 110,
				flex: 1,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
			},
			{
				field: "IsExpired",
				headerName: "Expired",
				minWidth: 90,
				maxWidth: 90,
				flex: 1,
				cellRenderer: renderExpiredCell,
				tooltipComponent: "agGridTooltip",
				tooltipValueGetter: (params: ITooltipParams) =>
					params.data?.IsExpired
						? `Work order open for ${params.data.DaysOpen} days`
						: null,
			},
			{
				field: "TypeCode",
				headerName: "Type",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderTypeCell,
			},
			{
				field: "CraftName",
				headerName: "Craft",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderCategoryCell,
			},
			{
				field: "DownHours",
				headerName: "Down Hours",
				minWidth: 80,
				maxWidth: 80,
				flex: 1,
			},
			{
				headerName: "",
				flex: 1,
				minWidth: 220,
				cellClass: "ag-columns",
				wrapText: false,
				autoHeight: false,
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<Grid2
							container
							style={{
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid2
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
								size={{ xs: 4, md: 4 }}
							>
								<WorkOrdersCommentsColumn
									searchData={refreshWorkOrders}
									params={params}
								/>
							</Grid2>
							<Grid2
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
								size={{ xs: 4, md: 4 }}
							>
								<WorkOrdersResourcesColumn
									params={params}
									setSelectedRow={setSelectedRow}
									openResourceModal={openResourceModal}
								/>
							</Grid2>
							<Grid2
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
								size={{ xs: 4, md: 4 }}
							>
								<Grid2
									container
									spacing={1}
									style={{ justifyContent: "flex-end", minWidth: 30 }}
								>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Button
											fullWidth
											style={{}}
											onClick={(e) => onActionsClick(e, params)}
										>
											<FormatListBulletedSharpIcon
												style={{ height: "auto" }}
												color="action"
											/>
										</Button>
									</Grid2>
								</Grid2>
							</Grid2>
						</Grid2>
					);
				},
			},
		]);
	}, [AssetId, openResourceModal]);

	return (
		<>
			{openWorkOrderResources && selectedResource && (
				<TableWorkOrdesResoures
					show={openWorkOrderResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenWorkOrderResources(false);
						setSelectedResource(null);
						if (shouldUpdate) {
							refreshWorkOrders();
						}
					}}
					AssetId={selectedRow?.AssetId}
					WorkOrderId={selectedRow?.WorkOrderId}
					initialResource={selectedResource}
					WorkOrderName={selectedRow?.WorkOrderName as string}
					Title={selectedRow?.Title as string}
					AssetName={selectedRow?.AssetName as string}
					TypeCode={selectedRow?.TypeCode as string}
					CraftId={selectedRow?.CraftId as number}
					OpenTimestamp={selectedRow?.OpenTimestamp as Date}
					CloseTimestamp={selectedRow?.CloseTimestamp as Date}
				/>
			)}
			<Grid2
				container
				sx={{
					padding: "10px 20px",
					height: "calc(100vh - 64px)",
					boxSizing: "border-box",
					flexDirection: "column",
					flexWrap: "nowrap",
				}}
			>
				<Grid2
					container
					size={{ xs: 12 }}
					alignItems="center"
					justifyContent="center"
					spacing={1}
					sx={{ flexShrink: 0 }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}
						>
							WORK ORDERS
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<FilterPaper sx={{ mt: 1, mb: 2 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ lg: 2, md: 12, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={AssetId ?? 0}
										onChange={changeAsset}
									/>
								</Grid2>

								<Grid2 size={{ lg: 10, md: 12, xs: 12 }}>
									<Filters onKeyDown={onKeyDown} />
								</Grid2>
							</Grid2>
						</FilterPaper>
					</Grid2>
				</Grid2>

				<Grid2
					size={{ xs: 12 }}
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Paper
						elevation={1}
						sx={{
							width: "100%",
							padding: "10px",
							borderRadius: "0.75rem",
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							boxSizing: "border-box",
						}}
					>
						<Grid2
							sx={{
								flex: 1,
								minHeight: 0,
								width: "100%",
							}}
							size={{ xs: 12 }}
						>
							<AgGridReact
								ref={gridRef}
								rowData={rowData}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								components={{ agGridTooltip: AgGridTooltip }}
								rowHeight={33}
								headerHeight={32}
								animateRows={true}
								loadingOverlayComponent={loadingOverlayComponent}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onDobleClicked}
								onSelectionChanged={onSelectionChanged}
								rowSelection={"multiple"}
								loading={isLoading}
								onGridReady={(params) => setGridApi(params.api)}
							/>
						</Grid2>
					</Paper>
				</Grid2>
			</Grid2>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={noResults && !noResultsDismissed}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="error" onClose={handleClose}>
					No results found
				</Alert>
			</Snackbar>
			<CloseGroupWorkOrdersDialog
				show={showDialogToCloseGroup}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
						setListWorkOrderSelected([]);
					}
					setShowDialogToCloseGroup(false);
				}}
				suffixTitle="Work Orders"
			/>
			<EditWorkOrderModal
				show={openWorkOrderEditModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenWorkOrderEditModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<CloseWorkOrderModalWithAttachments
				show={openCloseWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenCloseWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<OpenWorkOrderModal
				show={openWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<DeleteWorkOrderModal
				show={openDeleteWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenDeleteWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<DeleteMultipleWorkOrdersModal
				show={openDeleteMultipleWorkOrdersModal}
				data={selectedRows}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshWorkOrders();
					}
					setOpenDeleteMultipleWorkOrdersModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={
					workOrdersError && workOrdersError !== dismissedListError
						? workOrdersError.message
						: ""
				}
				onHide={() => setDismissedListError(workOrdersError)}
			/>
		</>
	);
}
